import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import { useTheme, useMediaQuery, Typography, IconButton, Tooltip, Button, Chip, Grid, Box } from "@mui/material";
import { alpha } from "@mui/system";
import { useHistory, useLocation } from "react-router-dom";
import { Column, Row, CellProps } from "react-table";
import { useIntl, defineMessage } from "react-intl";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import RefreshIcon from "@mui/icons-material/Refresh";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

import { Page } from "@mb-pro-ui/components";
import {
  EnhancedTable,
  FilterView as TableFilterView,
  Filter as TableFilter,
  // RemoteFilterViews,
  SimpleBooleanFilter,
  SimpleTextFilter,
  RemoteFilterViews,
} from "@mb-pro-ui/components/table";

import { Device, FilteredDeviceListItem } from "../utils/types";
import { DevicesReducerTypes, useDevices } from "../utils/DevicesProvider";
import { DeviceFilter, useServerCommunication } from "../utils/ServerCommunicaton";
import { isEqual } from "lodash-es";
import { useMutation } from "react-query";
import { CompanyAutocompleteFilter } from "../components/tablefilters/CompanyFilter";
import { usePageSxProps } from "./styles";
import { useApi } from "../utils/ApiProvider";
import { saveBlobToFile } from "../utils/utils";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { APP_NAME } from "../App";
import { HardwareTypeAutocompleteFilter } from "../components/tablefilters/HardwareTypeFilter";

export const useDeviceTableSettings = () => {
  const { formatMessage } = useIntl();

  const columnsNarrow: Column<FilteredDeviceListItem>[] = [
    {
      id: "ids",
      Header: formatMessage({
        defaultMessage: "IDs",
      }),
      accessor: (item) => [item.unitid, item.config_account].filter((i) => !!i).join(" "),
      Cell: ({ row }: CellProps<FilteredDeviceListItem>) => (
        <span style={{ height: 48, minHeight: 48 }}>
          <Typography component={"span"} sx={{ display: "block" }}>
            {row.original.unitid}
          </Typography>
          <Typography component={"span"} sx={{ display: "block" }}>
            {row.original.config_account}
          </Typography>
        </span>
      ),
      width: 75,
    },
    {
      id: "device",
      Header: formatMessage({
        defaultMessage: "Device",
      }),
      accessor: (item) => [item.config_name, item.config_description, item.hwtype, item.hwver].filter((i) => !!i).join(" "),
      Cell: ({ row }: CellProps<FilteredDeviceListItem>) => (
        <span style={{ height: 48, minHeight: 48 }}>
          <Typography component={"span"} sx={{ display: "block" }}>
            {row.original.config_name}
          </Typography>
          <Typography component={"span"} sx={{ display: "block" }}>
            {row.original.config_description}
          </Typography>
          <Typography component={"span"} sx={{ display: "block" }}>{`${row.original.hwtype}.${row.original.hwver}`}</Typography>
        </span>
      ),
      width: 100,
    },
    {
      id: "company",
      Header: formatMessage({
        defaultMessage: "Owner company",
      }),
      accessor: (item) => item.company_name,
      Cell: ({ row }: CellProps<FilteredDeviceListItem>) => (
        <span style={{ height: 48, minHeight: 48 }}>
          <Typography component={"span"} sx={{ display: "block" }}>
            {row.original.company_name}
          </Typography>
        </span>
      ),
      width: 120,
    },
  ];

  const columns: Column<FilteredDeviceListItem>[] = [
    {
      id: "unitid",
      Header: formatMessage({
        defaultMessage: "Helios ID",
      }),
      accessor: (item) => item.unitid,
      width: 75,
    },
    {
      id: "account",
      Header: formatMessage({
        defaultMessage: "Account",
      }),
      accessor: (item) => item.config_account,
      width: 100,
    },
    {
      id: "name",
      Header: formatMessage({
        defaultMessage: "Name",
      }),
      accessor: (item) => item.config_name,
      width: 100,
    },
    {
      id: "description",
      Header: formatMessage({
        defaultMessage: "Description",
      }),
      accessor: (item) => item.config_description,
      width: 100,
    },
    {
      id: "hwtype",
      Header: formatMessage({
        defaultMessage: "Hardware type",
      }),
      accessor: (item) => [item.hwtype, item.hwver].filter((i) => !!i).join("."),
      width: 120,
    },
    {
      id: "company",
      Header: formatMessage({
        defaultMessage: "Owner company",
      }),
      accessor: (item) => item.company_name,
      width: 120,
    },
  ];

  const tableFilters: TableFilterView[] = [
    {
      id: "unitid",
      Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Helios ID" }) }),
    },
    {
      id: "hwtype",
      Filter: HardwareTypeAutocompleteFilter,
    },
    {
      id: "hwver",
      Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Hardware version" }) }),
    },
    {
      id: "company",
      Filter: CompanyAutocompleteFilter,
    },
    {
      id: "account",
      Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Account" }) }),
    },
    {
      id: "name",
      Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Name" }) }),
    },
    {
      id: "description",
      Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Description" }) }),
    },
    {
      id: "without-template",
      Filter: SimpleBooleanFilter(defineMessage({ defaultMessage: "Show devices without template" }), null, "hideFalse"),
    },
  ];

  const getQueryFilter = (filter: TableFilter[]) => {
    const newFilter = { withTemplate: true } as DeviceFilter;
    filter.forEach(({ id, value }) => {
      if (id === "without-template" && value) {
        delete newFilter.withTemplate;
      }
      if (id === "unitid" && value) {
        newFilter.unitid = value;
      }
      if (id === "hwtype" && value) {
        newFilter.hwtype = value;
      }
      if (id === "hwver" && value) {
        const parsed = parseInt(value);
        newFilter.hwver = isNaN(parsed) ? 0 : parsed;
      }
      if (id === "company" && value) {
        newFilter.company = Array.isArray(value) ? value.map((v) => parseInt(v)) : value;
      }
      if (id === "account" && value) {
        newFilter.account = value;
      }
      if (id === "name" && value) {
        newFilter.name = value;
      }
      if (id === "description" && value) {
        newFilter.description = value;
      }
    });
    return newFilter;
  };

  return { columnsNarrow, columns, tableFilters, getQueryFilter };
};

export const DevicesPage = () => {
  const history = useHistory();
  const [filter, setFilter] = useState<DeviceFilter | null>(null);
  const queryControllerRef = useRef<AbortController | null>(null);
  const [selectedUnits, setSelectedUnits] = useState<number[] | null>(null);

  const theme = useTheme();
  const { listPageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const narrow = useMediaQuery("(max-width:1000px)", { noSsr: true });

  // TODO: át kell alakítani az új lekérdezésre, és akkor lehet így használni:
  // const { columns, columnsNarrow, tableFilters, getQueryFilter } = useDeviceTableSettings();
  const { getQueryFilter } = useDeviceTableSettings();

  const {
    state: { deviceList },
    dispatch,
  } = useDevices();
  const { getDeviceList } = useServerCommunication();
  const limit = 1000;
  const api = useApi();
  const location = useLocation();

  const { errorHandler: onError } = useErrorHandler([]);

  const exportQuery = useCallback(
    () => api(`/units/export`, { method: "post", headers: { "Content-Type": "application/json" }, body: JSON.stringify(selectedUnits) }),
    [api, selectedUnits]
  );
  const onExportSuccess = useCallback(
    (response) => {
      saveBlobToFile(response, selectedUnits?.join(","));
    },
    [selectedUnits]
  );
  const { mutate: exportConfig, isLoading: exportLoading } = useMutation({ mutationFn: exportQuery, onSuccess: onExportSuccess, onError });

  const getDevicesQuery = useCallback(() => {
    if (!filter) {
      return Promise.resolve();
    }
    if (queryControllerRef.current) {
      queryControllerRef.current.abort("outdated");
    }
    queryControllerRef.current = new AbortController();
    return getDeviceList({ ...filter, limit, signal: queryControllerRef.current.signal }).catch((err) => {
      if (err.message !== "outdated") {
        throw err;
      }
    });
  }, [filter, getDeviceList]);
  const onDeviceSuccess = useCallback(
    (devices) => {
      queryControllerRef.current = null;
      dispatch({ type: DevicesReducerTypes.SET_DEVICE_LIST, payload: { devices } });
    },
    [dispatch]
  );
  const { mutate: getDevices, status: queryStatus } = useMutation({ mutationFn: getDevicesQuery, onSuccess: onDeviceSuccess, onError });

  const getStyle = useCallback(
    (_item: Device, selected?: boolean) => {
      let color = selected ? theme.palette.primary.contrastText : theme.palette.common.black;
      let backgroundColor = selected ? theme.palette.primary.light : theme.palette.common.white;

      return { color, backgroundColor };
    },
    [theme]
  );

  const rowProps = useCallback(
    (row: Row<Device>) => ({
      style: {
        ...getStyle(row.original, selectedUnits?.includes(row.original.unitid)),
        "&:hover": {
          backgroundColor: (theme: any) => `${alpha(theme.palette.primary.main, 0.2)}`,
        },
      },
    }),
    [getStyle, selectedUnits]
  );

  const filterChanged = useCallback(
    (filter: TableFilter[]) => {
      setFilter((old) => {
        const newFilter = getQueryFilter(filter);
        if (isEqual(old, newFilter)) {
          return old;
        } else {
          sessionStorage.setItem("last_device_filter", location.search);
          return newFilter;
        }
      });
    },
    [getQueryFilter, location]
  );

  const turnOnUnitSelection = useCallback(() => {
    setSelectedUnits([]);
  }, []);

  const turnOffUnitSelection = useCallback(() => {
    setSelectedUnits(null);
  }, []);

  const toggleUnitSelection = useCallback((unitid: number) => {
    setSelectedUnits((old) => {
      if (!old) {
        return old;
      }
      return old.includes(unitid) ? old.filter((item) => item !== unitid) : [...old, unitid];
    });
  }, []);

  const openInNewTab = useCallback(
    (unitid: number) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      window.open(`/configuration/${unitid}`, "_blank", "noopener,noreferrer");
    },
    []
  );

  // useEffect(() => {
  //   const searchParams = new URLSearchParams(location.search);
  //   console.log("betöltéskor searchParams:", searchParams.toString(), searchParams.size);
  //   if (searchParams.size === 0) {
  //   }
  // }, [location.search]);

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Devices" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    if (filter === null) {
      return;
    }
    getDevices();
  }, [getDevices, filter]);

  // TODO: az új lekérdezéssel majd nem kell, hanem felül a useDeviceTableSettingsből
  const columnsNarrow = useMemo(
    (): Column<Device>[] => [
      {
        id: "ids",
        Header: formatMessage({
          defaultMessage: "IDs",
        }),
        accessor: (item) => [item.unitid, item.accountinfo?.account].filter((i) => !!i).join(" "),
        Cell: ({ row }: CellProps<Device>) => (
          <span style={{ height: 48, minHeight: 48 }}>
            <Typography component={"span"} sx={{ display: "block" }}>
              {row.original.unitid}
            </Typography>
            {row.original.accountinfo && (
              <Typography component={"span"} sx={{ display: "block" }}>
                {row.original.accountinfo.account}
              </Typography>
            )}
          </span>
        ),
        width: 75,
      },
      {
        id: "device",
        Header: formatMessage({
          defaultMessage: "Device",
        }),
        accessor: (item) =>
          [item.accountinfo?.name, item.accountinfo?.description, item.unitDetails?.hwtype, item.unitDetails?.hwver].filter((i) => !!i).join(" "),
        Cell: ({ row }: CellProps<Device>) => (
          <span style={{ height: 48, minHeight: 48 }}>
            {row.original.accountinfo && (
              <>
                <Typography component={"span"} sx={{ display: "block" }}>
                  {row.original.accountinfo.name}
                </Typography>
                <Typography component={"span"} sx={{ display: "block" }}>
                  {row.original.accountinfo.description}
                </Typography>
              </>
            )}
            {row.original.unitDetails && (
              <Typography component={"span"} sx={{ display: "block" }}>{`${row.original.unitDetails.hwtype}.${row.original.unitDetails.hwver}`}</Typography>
            )}
          </span>
        ),
        width: 100,
      },
      // {
      //   id: "hwtype",
      //   Header: formatMessage({
      //     defaultMessage: "Hardware type",
      //   }),
      //   accessor: (item) => (item.unitDetails ? [item.unitDetails.hwtype, item.unitDetails.hwver].filter((i) => !!i).join(".") : null),
      //   width: 120,
      // },
      {
        id: "company",
        Header: formatMessage({
          defaultMessage: "Owner company",
        }),
        accessor: (item) => item.company?.name,
        Cell: ({ row }: CellProps<Device>) => (
          <span style={{ height: 48, minHeight: 48 }}>
            <Typography component={"span"} sx={{ display: "block" }}>
              {row.original.company?.name}
            </Typography>
          </span>
        ),
        width: 120,
      },
    ],
    [formatMessage]
  );

  // TODO: az új lekérdezéssel majd nem kell, hanem felül a useDeviceTableSettingsből
  const columns = useMemo(
    (): Column<Device>[] => [
      {
        id: "unitid",
        Header: formatMessage({
          defaultMessage: "Helios ID",
        }),
        accessor: (item) => item.unitid,
        width: 75,
      },
      {
        id: "account",
        Header: formatMessage({
          defaultMessage: "Account",
        }),
        accessor: (item) => item.accountinfo?.account,
        width: 100,
      },
      {
        id: "name",
        Header: formatMessage({
          defaultMessage: "Name",
        }),
        accessor: (item) => item.accountinfo?.name,
        width: 100,
      },
      {
        id: "description",
        Header: formatMessage({
          defaultMessage: "Description",
        }),
        accessor: (item) => item.accountinfo?.description,
        width: 100,
      },
      {
        id: "hwtype",
        Header: formatMessage({
          defaultMessage: "Hardware type",
        }),
        accessor: (item) => (item.unitDetails ? [item.unitDetails.hwtype, item.unitDetails.hwver].filter((i) => !!i).join(".") : null),
        width: 120,
      },
      {
        id: "company",
        Header: formatMessage({
          defaultMessage: "Owner company",
        }),
        accessor: (item) => item.company?.name,
        width: 120,
      },
    ],
    [formatMessage]
  );

  // TODO: az új lekérdezéssel majd nem kell, hanem felül a useDeviceTableSettingsből
  const tableFilters = useMemo(
    (): TableFilterView[] => [
      {
        id: "unitid",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Helios ID" }) }),
      },
      {
        id: "hwtype",
        Filter: HardwareTypeAutocompleteFilter,
      },
      {
        id: "hwver",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Hardware version" }) }),
      },
      {
        id: "company",
        Filter: CompanyAutocompleteFilter,
      },
      {
        id: "account",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Account" }) }),
      },
      {
        id: "name",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Name" }) }),
      },
      {
        id: "description",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Description" }) }),
      },
      {
        id: "without-template",
        Filter: SimpleBooleanFilter(defineMessage({ defaultMessage: "Show devices without template" }), null, "hideFalse"),
      },
    ],
    [formatMessage]
  );

  const selectedUnitChips = useMemo(() => {
    if (!selectedUnits) {
      return null;
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Grid container spacing={0.5} sx={{ mt: 1, flex: 1 }}>
          {selectedUnits.map((unitid) => (
            <Grid item key={`selected-${unitid}`}>
              <Chip label={unitid} color="primary" onDelete={() => toggleUnitSelection(unitid)} />
            </Grid>
          ))}
        </Grid>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          sx={{ mt: 1 }}
          disabled={selectedUnits.length === 0 || exportLoading}
          onClick={() => exportConfig()}
        >
          {formatMessage({ defaultMessage: "Export" })}
        </Button>
      </Box>
    );
  }, [exportConfig, exportLoading, formatMessage, selectedUnits, toggleUnitSelection]);

  return (
    <Page sx={listPageSx}>
      <EnhancedTable
        columns={narrow ? columnsNarrow : columns}
        data={deviceList ?? []}
        showResultCount
        getRowProps={rowProps}
        queryStatus={queryStatus}
        overscanCount={5}
        onRowClick={
          selectedUnits === null
            ? (item: Row<Device>) => history.push(`/configuration/${item.original.unitid}`)
            : (item: Row<Device>) => toggleUnitSelection(item.original.unitid)
        }
        rowHeight={narrow ? 52 : undefined}
        actionPosition="left"
        actionCellWidth={60}
        action={({ original }) => (
          <IconButton sx={{ color: (theme) => theme.palette.secondary.main }} onClick={openInNewTab(original.unitid)}>
            <Tooltip
              title={formatMessage({
                defaultMessage: "Open in new tab",
              })}
            >
              <OpenInNewIcon />
            </Tooltip>
          </IconButton>
        )}
        postfix={
          <>
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                color: "primary.contrastText",
                mr: 1,
              }}
              startIcon={selectedUnits === null ? <ChecklistRtlIcon /> : <CloseIcon />}
              onClick={selectedUnits === null ? turnOnUnitSelection : turnOffUnitSelection}
            >
              {selectedUnits === null ? formatMessage({ defaultMessage: "Select for export" }) : formatMessage({ defaultMessage: "Quit from selection" })}
            </Button>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={() => getDevices()}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Reload",
                })}
              >
                <RefreshIcon />
              </Tooltip>
            </IconButton>
          </>
        }
        filters={(opts) => <RemoteFilterViews {...opts} tableFilters={tableFilters} onFilterChange={filterChanged} />}
        limit={limit}
      />
      {selectedUnitChips}
    </Page>
  );
};

export default DevicesPage;
